/**
 * SPDX-FileCopyrightText: 2026 Conduction B.V.
 * SPDX-License-Identifier: EUPL-1.2
 *
 * Resolve dynamic `@`-tokens inside a widget filter at FETCH time, so manifest
 * filters can express "relative to now / the current user" without baking a
 * fixed value. The widgets call this just before sending the filter to
 * OpenRegister.
 *
 * Supported tokens:
 *  - `@me`                → the current Nextcloud user id (`getCurrentUser()` from
 *                           `@nextcloud/auth`; `window.OC.currentUser` fallback)
 *  - `@now`               → the current instant, ISO-8601
 *  - `@today`             → today at 00:00, `YYYY-MM-DD` (date-prefix comparable)
 *  - `@today±Nd`          → relative-date arithmetic at day granularity: N days
 *                           from today (e.g. `@today-30d`, `@today+7d`)
 *  - `@monthStart`        → first day of the current month, `YYYY-MM-DD`
 *  - `@quarterStart`      → first day of the current quarter, `YYYY-MM-DD`
 *  - `@yearStart`         → first day of the current year, `YYYY-MM-DD`
 *  - `@currentFiscalYear` → the current calendar year as a number string, e.g. `2026`
 *  - `@objectId`          → the current detail-page object's id (needs `ctx`)
 *  - `@object.<field>`    → a field off the current detail-page object (needs `ctx`)
 *  - `@workspace.<key>`   → a value off the page-level workspace context (needs `ctx.workspace`)
 *  - `@config.<key>`      → a value off the page-level app config (needs `ctx.config`)
 *
 * `@objectId` / `@object.<field>` are OBJECT-CONTEXT tokens: they resolve only
 * when a `ctx` `{ objectId, object }` is supplied (a detail page provides it via
 * the `cnObjectContext` inject). `@workspace.<key>` is a WORKSPACE-CONTEXT token:
 * it resolves a key off `ctx.workspace` (the reactive `cnWorkspaceContext` a
 * dashboard/workspace page provides) so a list widget can react to page-level
 * state another widget writes (e.g. a selected client). Without the matching
 * context the tokens pass through unchanged — and an UNRESOLVED `@workspace.*`
 * token signals "no selection yet" to the caller (see {@link hasUnresolvedTokens}).
 *
 * `@config.<key>` is an APP-CONFIG token: it resolves a key off `ctx.config` (the
 * page-level app config a dashboard/detail page provides via `cnAppConfig` — e.g.
 * a reporting `currency` captured by the setup wizard). Like `@workspace.<key>`, a
 * trailing `?` marks it optional. An UNRESOLVED required `@config.<key>` passes
 * through unchanged; this lets `format: { currency: '@config.currency' }` fall
 * back to a literal default when the config is unset.
 *
 * IN-lists: a filter value MAY be an ARRAY (`{ status: ['open', 'pending'] }`,
 * or the explicit operator form `{ status: { in: [...] } }`). Tokens inside the
 * array are resolved item-by-item (`{ assignee: ['@me', 'shared'] }` works).
 * OpenRegister expects an IN-list as repeated bracket params
 * (`status[]=open&status[]=pending` — the same shape `buildQueryString` /
 * `useObjectStore` send), so widget fetchers serialize a resolved array as
 * repeated `field[]` params, never as a JSON blob or a single joined string.
 *
 * @module utils/resolveFilterTokens
 */

import { getCurrentUser } from '@nextcloud/auth'
import { TODAY_DELTA_RE } from './sentinelTokens.js'

/**
 * Format a Date as `YYYY-MM-DD` (local).
 *
 * @param {Date} d The date.
 * @return {string} The date-only string.
 */
function ymd(d) {
	const m = String(d.getMonth() + 1).padStart(2, '0')
	const day = String(d.getDate()).padStart(2, '0')
	return `${d.getFullYear()}-${m}-${day}`
}

/**
 * Resolve a single filter value if it is a dynamic `@`-token, else pass through.
 *
 * @param {*} v The candidate value.
 * @param {{objectId?: (string|number), object?: object, workspace?: object, config?: object}} [ctx] Optional
 *   context for `@objectId` / `@object.<field>` (detail page),
 *   `@workspace.<key>` (page-level workspace state), and `@config.<key>`
 *   (page-level app config) tokens.
 * @return {*} The resolved value.
 */
export function resolveFilterValue(v, ctx) {
	if (typeof v !== 'string' || v.charAt(0) !== '@') return v
	const now = new Date()
	if (v === '@objectId') {
		return (ctx && ctx.objectId !== undefined && ctx.objectId !== null) ? String(ctx.objectId) : v
	}
	if (v.startsWith('@object.')) {
		const field = v.slice('@object.'.length)
		if (ctx && ctx.object && field && ctx.object[field] !== undefined) return ctx.object[field]
		return v
	}
	if (v.startsWith('@workspace.')) {
		// A trailing `?` marks the token OPTIONAL: when unset the caller drops the
		// filter key (show all) instead of waiting (see hasUnresolvedTokens /
		// CnObjectListWidget). The `?` is stripped from the key lookup.
		const raw = v.slice('@workspace.'.length)
		const key = raw.endsWith('?') ? raw.slice(0, -1) : raw
		if (ctx && ctx.workspace && key && ctx.workspace[key] !== undefined
			&& ctx.workspace[key] !== null && ctx.workspace[key] !== '') {
			return ctx.workspace[key]
		}
		return v
	}
	if (v.startsWith('@config.')) {
		// A trailing `?` marks the token OPTIONAL (same convention as
		// `@workspace.<key>?`): an unset value lets the caller drop the key. A
		// required `@config.<key>` that is unset passes through unchanged so a
		// downstream consumer can fall back to a literal default (e.g.
		// `format.currency` → `'EUR'`).
		const raw = v.slice('@config.'.length)
		const key = raw.endsWith('?') ? raw.slice(0, -1) : raw
		if (ctx && ctx.config && key && ctx.config[key] !== undefined
			&& ctx.config[key] !== null && ctx.config[key] !== '') {
			return ctx.config[key]
		}
		return v
	}
	if (v === '@me') {
		// Canonical source is @nextcloud/auth; window.OC is the fallback for
		// environments where the auth package can't read the page state (jsdom).
		let uid = null
		try {
			const user = getCurrentUser()
			uid = user && user.uid
		} catch (e) {
			uid = null
		}
		return uid || (typeof window !== 'undefined' && window.OC && window.OC.currentUser) || ''
	}
	if (v === '@now') return now.toISOString()
	if (v === '@today') {
		const d = new Date(now)
		d.setHours(0, 0, 0, 0)
		return ymd(d)
	}
	if (v === '@monthStart') {
		return ymd(new Date(now.getFullYear(), now.getMonth(), 1))
	}
	if (v === '@quarterStart') {
		return ymd(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1))
	}
	if (v === '@yearStart') {
		return ymd(new Date(now.getFullYear(), 0, 1))
	}
	if (v === '@currentFiscalYear') {
		return String(now.getFullYear())
	}
	const m = v.match(TODAY_DELTA_RE)
	if (m) {
		const d = new Date(now)
		d.setHours(0, 0, 0, 0)
		d.setDate(d.getDate() + Number(m[1]))
		return ymd(d)
	}
	return v
}

/**
 * Whether a value is an UNRESOLVED OPTIONAL token — a string still shaped like
 * `@workspace.<key>?` or `@config.<key>?` after token resolution (the `?` marks
 * it as "drop me when unset"). Callers strip such keys from the filter so the
 * list shows all rows rather than waiting for a selection / a config value.
 *
 * @param {*} v A (possibly resolved) filter value.
 * @return {boolean}
 */
export function isOptionalUnresolved(v) {
	return typeof v === 'string'
		&& (v.startsWith('@workspace.') || v.startsWith('@config.'))
		&& v.endsWith('?')
}

/**
 * Return a copy of a resolved filter map with every UNRESOLVED OPTIONAL
 * workspace token (`@workspace.<key>?` left as-is) removed. Required tokens and
 * concrete values are kept untouched. Inside an IN-list array, unresolved
 * optional ITEMS are dropped; a key whose array empties out is dropped whole
 * (an empty IN-list means "no constraint", not "match nothing").
 *
 * @param {object} filter A filter map already passed through {@link resolveFilterTokens}.
 * @return {object} The filter without optional-unresolved keys.
 */
export function dropOptionalUnresolved(filter) {
	if (!filter || typeof filter !== 'object') return filter
	const out = {}
	for (const [k, v] of Object.entries(filter)) {
		if (isOptionalUnresolved(v)) continue
		if (Array.isArray(v)) {
			const items = v.filter((item) => !isOptionalUnresolved(item))
			if (items.length > 0) out[k] = items
		} else if (v && typeof v === 'object') {
			const inner = {}
			for (const [op, ov] of Object.entries(v)) {
				if (Array.isArray(ov)) {
					const items = ov.filter((item) => !isOptionalUnresolved(item))
					if (items.length > 0) inner[op] = items
				} else if (!isOptionalUnresolved(ov)) {
					inner[op] = ov
				}
			}
			if (Object.keys(inner).length > 0) out[k] = inner
		} else {
			out[k] = v
		}
	}
	return out
}

/**
 * Whether a resolved filter map still carries an unresolved `@`-token. Used by
 * context-bound widgets to detect "the page state this widget depends on isn't
 * set yet" (e.g. a client-overview list whose `client` filter is still the raw
 * `@workspace.selectedClient` because no client is selected) and skip the query
 * / render a prompt instead of fetching the whole register.
 *
 * OPTIONAL tokens (`@workspace.<key>?`) are NOT counted — they are meant to be
 * dropped, not waited on (see {@link dropOptionalUnresolved}).
 *
 * @param {object} filter A filter map already passed through {@link resolveFilterTokens}.
 * @return {boolean} True when any value is still a (non-optional) string beginning with `@`.
 */
export function hasUnresolvedTokens(filter) {
	if (!filter || typeof filter !== 'object') return false
	const blocking = (x) => typeof x === 'string' && x.charAt(0) === '@' && !isOptionalUnresolved(x)
	const anyBlocking = (v) => {
		if (blocking(v)) return true
		if (Array.isArray(v)) return v.some(blocking)
		return false
	}
	for (const v of Object.values(filter)) {
		if (anyBlocking(v)) return true
		if (v && typeof v === 'object' && !Array.isArray(v)) {
			for (const ov of Object.values(v)) {
				if (anyBlocking(ov)) return true
			}
		}
	}
	return false
}

/**
 * Resolve every dynamic `@`-token in a filter map (equality, operator, and
 * IN-list array shapes). Array values resolve item-by-item so a list can mix
 * tokens and literals (`{ assignee: ['@me', 'shared'] }`).
 *
 * @param {object} filter The filter map (`{ field: value | value[] | { op: value | value[] } }`).
 * @param {{objectId?: (string|number), object?: object, workspace?: object, config?: object}} [ctx] Optional
 *   context forwarded to {@link resolveFilterValue} for `@objectId` /
 *   `@object.<field>` / `@workspace.<key>` / `@config.<key>` tokens.
 * @return {object} A new filter map with tokens resolved.
 */
export function resolveFilterTokens(filter, ctx) {
	if (!filter || typeof filter !== 'object') return filter
	const resolveOne = (v) => (Array.isArray(v)
		? v.map((item) => resolveFilterValue(item, ctx))
		: resolveFilterValue(v, ctx))
	const out = {}
	for (const [k, v] of Object.entries(filter)) {
		if (v && typeof v === 'object' && !Array.isArray(v)) {
			const inner = {}
			for (const [op, ov] of Object.entries(v)) inner[op] = resolveOne(ov)
			out[k] = inner
		} else {
			out[k] = resolveOne(v)
		}
	}
	return out
}

/**
 * Recursively resolve `@`-tokens inside an arbitrary JSON value — objects
 * and arrays at ANY depth, unlike {@link resolveFilterTokens} which only
 * understands the flat filter-map shape (equality / operator / IN-list, one
 * level of nesting). Used by the `api-call` action's `payload`: a
 * Filinq-style generation body like
 * `{ dataRefs: [{ register: 'crm', schema: 'lead', id: '@objectId' }] }`
 * needs the token resolved inside a nested array of objects, which the
 * shallow filter-map resolver can't reach.
 *
 * @param {*} value The value to resolve — object / array / primitive.
 * @param {{objectId?: (string|number), object?: object, workspace?: object, config?: object}} [ctx] Optional
 *   context forwarded to {@link resolveFilterValue}.
 * @return {*} A new value with every string leaf token-resolved (structure preserved).
 */
export function resolveDeepTokens(value, ctx) {
	if (Array.isArray(value)) return value.map((v) => resolveDeepTokens(v, ctx))
	if (value && typeof value === 'object') {
		const out = {}
		for (const [k, v] of Object.entries(value)) out[k] = resolveDeepTokens(v, ctx)
		return out
	}
	return resolveFilterValue(value, ctx)
}

/**
 * Deep-tree counterpart to {@link hasUnresolvedTokens}: whether an
 * already-{@link resolveDeepTokens}-resolved value still carries an
 * unresolved, non-optional `@`-token anywhere inside it (any depth). Used to
 * BLOCK an `api-call` whose `payload` has a required token (typically
 * `@objectId` / `@object.<field>`) that failed to resolve, instead of
 * silently sending the literal token string to the server.
 *
 * @param {*} value The (already deep-resolved) value.
 * @return {boolean} True when a required token is still unresolved.
 */
export function hasUnresolvedDeepTokens(value) {
	if (Array.isArray(value)) return value.some(hasUnresolvedDeepTokens)
	if (value && typeof value === 'object') return Object.values(value).some(hasUnresolvedDeepTokens)
	return typeof value === 'string' && value.charAt(0) === '@' && !isOptionalUnresolved(value)
}

/**
 * Deep-tree counterpart to {@link dropOptionalUnresolved}: strips unresolved
 * OPTIONAL tokens (`@workspace.<key>?` / `@config.<key>?` left as-is after
 * resolution) out of an arbitrary JSON value at any depth. An object key
 * whose value is an unresolved optional token is dropped entirely; an array
 * item that resolves unresolved-optional is filtered out.
 *
 * @param {*} value The (already {@link resolveDeepTokens}-resolved) value.
 * @return {*} The value with unresolved-optional leaves removed.
 */
export function dropOptionalUnresolvedDeep(value) {
	if (Array.isArray(value)) {
		return value
			.filter((v) => !isOptionalUnresolved(v))
			.map((v) => dropOptionalUnresolvedDeep(v))
	}
	if (value && typeof value === 'object') {
		const out = {}
		for (const [k, v] of Object.entries(value)) {
			if (isOptionalUnresolved(v)) continue
			out[k] = dropOptionalUnresolvedDeep(v)
		}
		return out
	}
	return value
}
